import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { TagSelect } from '../components/TagSelect';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function NewDashboardPage() {
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadVisibility, setUploadVisibility] = useState<'private' | 'discoverable'>('discoverable');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadTags, setUploadTags] = useState<string[]>([]);
  const [uploadCanvasType, setUploadCanvasType] = useState<'prototype' | 'dashboard' | ''>('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const screenshotInputRef = useRef<HTMLInputElement>(null);

  const uploadScreenshot = async (dashboardId: string) => {
    if (!screenshotFile) return;
    const { presignedUrl, screenshotKey } = await api.getScreenshotUploadUrl(dashboardId, screenshotFile.type);
    await fetch(presignedUrl, { method: 'PUT', body: screenshotFile, headers: { 'Content-Type': screenshotFile.type } });
    await api.confirmScreenshot(dashboardId, screenshotKey);
  };

  const navigate = useNavigate();

  useEffect(() => {
    api.listTags().then((r) => setAvailableTags(r.tags ?? [])).catch(() => {});
  }, []);

  const ACCEPTED = ['.html', '.htm'];

  const selectFile = (file: File | null) => {
    setUploadError('');
    if (!file) {
      setUploadFile(null);
      return;
    }
    const ok = ACCEPTED.some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!ok) {
      setUploadFile(null);
      setUploadError('Unsupported file type. Upload an .html file.');
      return;
    }
    setUploadFile(file);
    // Pre-fill the title from the filename if the user hasn't set one.
    if (!uploadTitle.trim()) {
      setUploadTitle(file.name.replace(/\.[^.]+$/, ''));
    }
  };

  const clearFile = () => {
    setUploadFile(null);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    selectFile(e.dataTransfer.files?.[0] ?? null);
  };

  const submitUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    setUploadError('');
    try {
      const { dashboardId, presignedUrl } = await api.requestUpload({
        title: uploadTitle,
        visibility: uploadVisibility,
        filename: uploadFile.name,
        tags: uploadTags,
        category: 'html',
        canvas_type: uploadCanvasType as 'prototype' | 'dashboard',
      });

      const putRes = await fetch(presignedUrl, {
        method: 'PUT',
        body: uploadFile,
        headers: { 'Content-Type': 'text/html' },
      });
      if (!putRes.ok) throw new Error('File upload failed');

      await api.completeUpload(dashboardId);
      await uploadScreenshot(dashboardId);
      navigate(`/dashboard/${dashboardId}`);
    } catch (err: unknown) {
      setUploadError((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="narrow">
      <h1>New Upload</h1>
      <form className="card form" onSubmit={submitUpload}>
        <p className="muted">Upload a self-contained <code>.html</code> file with its CSS/JS/images inlined.</p>
        <div
          className={`dropzone ${dragActive ? 'drag' : ''} ${uploadFile ? 'has-file' : ''}`}
          onClick={() => !uploadFile && fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
          onDrop={onDrop}
          onKeyDown={(e) => {
            if (!uploadFile && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Upload dashboard file"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm"
            hidden
            onChange={(e) => selectFile(e.target.files?.[0] ?? null)}
          />
          {uploadFile ? (
            <div className="dropzone-file">
              <span className="dropzone-icon">📄</span>
              <div className="dropzone-meta">
                <strong>{uploadFile.name}</strong>
                <span className="muted">{formatBytes(uploadFile.size)}</span>
              </div>
              <button
                type="button"
                className="btn ghost"
                onClick={(e) => { e.stopPropagation(); clearFile(); }}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="dropzone-prompt">
              <span className="dropzone-icon">📥</span>
              <strong>Drop your file here, or click to browse</strong>
              <span className="muted">Accepts .html or .htm</span>
            </div>
          )}
        </div>
        <label>
          Title
          <input
            value={uploadTitle}
            onChange={(e) => setUploadTitle(e.target.value)}
            placeholder="My Canvas"
            maxLength={200}
          />
        </label>
        <label>
          Canvas Type
          <select
            value={uploadCanvasType}
            onChange={(e) => setUploadCanvasType(e.target.value as 'prototype' | 'dashboard')}
            required
          >
            <option value="">Select a type…</option>
            <option value="prototype">Prototype</option>
            <option value="dashboard">Dashboard</option>
          </select>
        </label>
        <div className="field">
          <span className="field-label">Tags</span>
          <TagSelect options={availableTags} value={uploadTags} onChange={setUploadTags} />
        </div>
        <label>
          Visibility
          <select
            value={uploadVisibility}
            onChange={(e) => setUploadVisibility(e.target.value as 'private' | 'discoverable')}
          >
            <option value="private">Private (only you and people you share with)</option>
            <option value="discoverable">Discoverable (appears in Canvas browser)</option>
          </select>
        </label>
        <div className="field">
          <span className="field-label">Screenshot <span className="muted">(optional thumbnail)</span></span>
          {screenshotFile ? (
            <div className="screenshot-preview">
              <img src={URL.createObjectURL(screenshotFile)} alt="Screenshot preview" className="screenshot-thumb" />
              <button type="button" className="btn ghost" onClick={() => { setScreenshotFile(null); if (screenshotInputRef.current) screenshotInputRef.current.value = ''; }}>Remove</button>
            </div>
          ) : (
            <button type="button" className="btn" onClick={() => screenshotInputRef.current?.click()}>Choose image…</button>
          )}
          <input ref={screenshotInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={(e) => setScreenshotFile(e.target.files?.[0] ?? null)} />
        </div>
        {uploadError && <p className="error">{uploadError}</p>}
        <button type="submit" className="btn success" disabled={uploading || !uploadFile || !uploadCanvasType}>
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </form>
    </div>
  );
}
